/**
 * Phase: pre-explosion（progress === 0）。
 *
 * spec §3.3 / §8 / wireframe L736 に従い、無傷の innerPoly を surfaceColor で
 * ベタ塗り描画する。Voronoi 分割は一切描かない（D-13 二相モデル）。
 *
 * 2026-04 Phase C 修正ラウンド Step 2（4 層モデル再設計）:
 * 旧実装は outer 全面を innerColor で塗ってから Surface を重ねていたが、これは
 * 「Inner 層が silhouette の外にも漏れる」誤った実装だった。
 * 正しい 4 層モデルでは Background（`renderer.ts` が担当）→ Inner（silhouette
 * のみ、explosion 相でのみ描画）→ Surface（破片）→ Text。progress=0 では破片が
 * 動いていない＝Inner 下敷きは完全に隠れているため、innerPoly に Surface を
 * 1 回塗るだけで十分。Inner 全体塗りは削除した。
 *
 * 2026-04 Phase E Step 4（PNG Overlay composite）:
 * Surface 塗りを「solid surface color」から「surface color + overlay PNG の
 * composite canvas」に置き換える。呼び出し側 `renderer.ts` が composite を
 * 構築済みで渡し、本関数は silhouette で clip → `drawImage(composite)` の
 * 形で合成する。`composite === null` のときは従来の solid fill にフォール
 * バックする（overlay なしの既存挙動を完全維持）。
 *
 * 描画順:
 * 1. silhouette を surfaceColor でソリッド fill（composite=null の場合）
 *    または silhouette で clip → drawImage(composite) （composite 非 null）
 *
 * 設計参照:
 * - docs/happa-designtool-v1.1-spec.md §3.3（二相レンダリング）
 * - docs/happa-designtool-v1.1-spec.md §8（レンダリングパイプライン）
 * - docs/happa-designtool-v1.1-spec.md Phase E Step 4（PNG Overlay）
 * - wireframes/v0.10/wireframe.html L736–749
 */

import type { Layout, Scene, Vec2 } from '../../core/types';
import type { AppState } from '../../state';

/**
 * polygon を ctx の現在パスへ moveTo / lineTo / closePath で積む共通 helper。
 *
 * 頂点数 < 3 または先頭頂点が取れない退化入力では何もせず抜ける。
 * `ctx.beginPath()` は呼ばない（clip / fill 両用のため呼び出し側で起点管理）。
 */
function tracePolyOnCtx(ctx: CanvasRenderingContext2D, poly: Vec2[]): void {
  if (poly.length < 3) return;
  const first = poly[0];
  if (!first) return;
  ctx.moveTo(first[0], first[1]);
  for (let i = 1; i < poly.length; i++) {
    const p = poly[i];
    if (!p) continue;
    ctx.lineTo(p[0], p[1]);
  }
  ctx.closePath();
}

/**
 * 無傷シーン（progress = 0）を 1 フレーム描画する。
 *
 * `ctx` のサイズは呼び出し側が `Layout.outerW/H` に同期済みである前提。
 * 本関数は `ctx.save()`/`restore()` で描画状態をカプセル化し、呼び出し側の
 * `fillStyle` や `globalAlpha` を汚染しない。
 *
 * Background 層（`state.backgroundColor` / `backgroundTransparent`）は呼び出し側
 * `renderer.ts` が本関数より前に塗っている前提。本関数は Surface 層のみを描く。
 *
 * 2026-04 silhouette リファクタ:
 * `silhouette` 引数は `renderer.ts` が `buildSilhouette(state, scene, t?)` で
 * 計算する polygon。Phase C までの warp 倍率 0 時は `scene.innerPoly` と
 * 一致したが、**Phase D で Breath Motion（呼吸）を適用するようになり
 * progress=0 でも silhouette は刻々と変化する**。Surface を呼吸させるため
 * には scene.innerPoly キャッシュではなく silhouette 引数を fill 経路に
 * 使う必要がある（Bug 報告 2026-04-20: Surface が静止、text だけ呼吸する
 * 症状の修正）。
 *
 * 2026-04 Phase E Step 4 追加引数 `composite`:
 * overlay PNG を surface と合成した offscreen canvas。`renderer.ts` が
 * `state.overlayImage !== null` のときだけ `buildComposite()` を呼んで用意し、
 * 本関数に渡す。`null` のときは既存の solid fill パスで動く（overlay なしの
 * 回帰なしを保証）。
 *
 * @param ctx        描画先の 2D コンテキスト。
 * @param _L         現在の Layout（Phase C 修正で全面 fill が不要になり未使用）。
 * @param _scene     `buildScene` の結果（Phase D 以降は silhouette を直接使うため未参照）。
 * @param state      現在の AppState。色（`surfaceColor`）のみ参照。
 * @param silhouette renderer.ts が `buildSilhouette` で計算した silhouette。
 *                   progress=0 で Breath amp>0 なら呼吸で形が変わる。
 * @param composite  Phase E Step 4: surface + overlay 合成済み canvas。
 *                   null なら solid surface fill にフォールバック。
 */
export function renderPreExplosion(
  ctx: CanvasRenderingContext2D,
  _L: Layout,
  _scene: Scene,
  state: AppState,
  silhouette: Vec2[],
  composite: HTMLCanvasElement | null,
): void {
  if (silhouette.length < 3) return;

  ctx.save();

  if (composite === null) {
    // ── overlay なしの既存挙動（solid fill）──────────────────
    // silhouette を surfaceColor でソリッド fill（spec §3.3 + Phase D Breath）。
    // Voronoi cells は描かない（二相モデル「progress=0 では分割線を見せない」）。
    // Background 層は renderer.ts が既に塗っているので、Inner 全体塗りは不要
    // （破片が動いていないため Inner 下敷きは視覚的に完全に隠れる）。
    ctx.fillStyle = state.surfaceColor;
    ctx.beginPath();
    tracePolyOnCtx(ctx, silhouette);
    ctx.fill();
  } else {
    // ── overlay ありの composite 合成パス（Phase E Step 4）─────
    // silhouette で clip を張り、composite（surface + overlay）を outer 座標
    // そのまま drawImage。composite は outer 寸法で構築済みなので offset 0。
    // clip は save/restore スコープ内に閉じ込めてあるので、ctx.restore() で
    // 解除される（呼び出し側の clip 状態を汚さない）。
    ctx.beginPath();
    tracePolyOnCtx(ctx, silhouette);
    ctx.clip();
    ctx.drawImage(composite, 0, 0);
  }

  ctx.restore();
}
